/**
 * Penyimpan token akses layar kasir.
 *
 * ### ⛔ Peramban TIDAK memegang kunci privat perangkat
 * `private_key_jwt` (RFC 7523) menuntut kunci privat. Menaruhnya di `localStorage` berarti setiap
 * skrip pihak ketiga — dan setiap ekstensi peramban — memegang identitas perangkat **selamanya**,
 * sementara token yang bocor mati dalam 15 menit (`ACCESS_TOKEN_TTL_SECONDS`). Karena itu kunci
 * tinggal di **`device-agent`** (K4: agen lokal kecil), dan yang sampai ke peramban hanyalah token
 * akses berumur pendek.
 *
 * Tiga sumber, berurutan — dan urutannya disengaja:
 * 1. **`device-agent`** lokal (`/agent/token`) — jalur sesungguhnya di terminal kasir.
 * 2. `localStorage` — token yang sudah didapat sebelumnya, selama belum kedaluwarsa.
 * 3. `VITE_POS_TOKEN` — **hanya pengembangan & uji e2e**.
 *
 * ⚠️ Tak ada sumber keempat berupa "tanpa token". Layar yang boleh jalan tanpa token akan dipakai
 * tanpa token, dan tenant-nya harus datang dari suatu tempat — persis yang dilarang R-AI-1.
 */
const KEY = 'osbpos.token';
const STAFF_KEY = 'osbpos.staf';
/**
 * ⭐⭐ **Rahasia perangkat** — hasil pemasangan, dan satu-satunya benda di peramban yang berumur
 * panjang.
 *
 * ⛔ Ini BUKAN kunci privat `private_key_jwt` (lihat catatan di kepala berkas): ia rahasia acak
 * yang **dicabut sepihak dari portal** dengan menonaktifkan perangkatnya. Bedanya penting — kunci
 * privat yang bocor memberi identitas perangkat selamanya, rahasia ini berhenti berlaku pada
 * penukaran token berikutnya.
 */
const SECRET_KEY = 'osbpos.device';

let memoryCache: string | undefined;

/** Alamat agen lokal. Diproxy Vite di pengembangan supaya tak kena CORS. */
const AGEN = '/agent/token';

export function storedToken(): string | undefined {
  if (memoryCache !== undefined) return memoryCache;
  const from =
    globalThis.localStorage?.getItem(KEY) ??
    (import.meta.env['VITE_POS_TOKEN'] as string | undefined) ??
    undefined;
  if (from !== undefined && from !== '') memoryCache = from;
  return memoryCache;
}

export function saveToken(token: string): void {
  memoryCache = token;
  globalThis.localStorage?.setItem(KEY, token);
}

/**
 * Membuang token — dipanggil saat server menjawab 401.
 *
 * ⚠️ Menyimpan token yang sudah ditolak berarti setiap permintaan berikutnya gagal dengan cara yang
 * sama, dan kasir melihat "galat" alih-alih "masuk ulang".
 */
export function lupakanToken(): void {
  memoryCache = undefined;
  globalThis.localStorage?.removeItem(KEY);
}

/* ═══════════════ pemasangan perangkat (pairing) ══════════════════════════ */

export interface DevicePairing {
  readonly tenantCode: string;
  readonly deviceCode: string;
  readonly secret: string;
}

export function devicePairing(): DevicePairing | undefined {
  const raw = globalThis.localStorage?.getItem(SECRET_KEY);
  if (raw === null || raw === undefined || raw === '') return undefined;
  try {
    return JSON.parse(raw) as DevicePairing;
  } catch {
    globalThis.localStorage?.removeItem(SECRET_KEY);
    return undefined;
  }
}

export function saveDevicePairing(p: DevicePairing): void {
  globalThis.localStorage?.setItem(SECRET_KEY, JSON.stringify(p));
}

/**
 * Membuang pemasangan yang sudah **ditolak server**.
 *
 * ⛔ Bukan "membersihkan cache": inilah yang membuat layar jatuh ke pintu pemasangan alih-alih ke
 * jalan buntu berpesan "kode staf atau PIN salah".
 */
export function lupakanDevicePairing(): void {
  globalThis.localStorage?.removeItem(SECRET_KEY);
}

/**
 * ⭐⭐⭐ **Menukar rahasia perangkat dengan token baru** — pengganti `device-agent` di tablet.
 *
 * Token perangkat berumur 10 jam (satu shift). Tanpa jalur ini, terminal berhenti melayani tamu
 * tiap kali tokennya habis, dan tak ada seorang pun di outlet yang bisa memperbaikinya.
 *
 * ⛔ Mengembalikan `undefined` bila gagal — termasuk saat perangkatnya sudah DINONAKTIFKAN di
 * portal. Itu bukan kecelakaan: di situlah pencabutan bekerja, dan layar wajib jatuh ke pemasangan
 * ulang alih-alih mencoba selamanya.
 */
export async function tokenDariRahasia(): Promise<string | undefined> {
  const p = devicePairing();
  if (p === undefined) return undefined;
  try {
    const r = await fetch('/api/devices/token', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ tenantCode: p.tenantCode, deviceCode: p.deviceCode, secret: p.secret }),
    });
    /*
     * ⛔⛔⛔ **401 = rahasianya DITOLAK server ⇒ pemasangan ini sudah mati.**
     *
     * Dua sebab, dan keduanya final: perangkatnya dinonaktifkan di portal, atau perangkat yang sama
     * dipasangkan ULANG di tempat lain (satu perangkat = satu rahasia; pemasangan baru mencabut
     * yang lama — itu memang gunanya).
     *
     * ⛔ Menahannya berarti tablet terjebak: `App` melihat "masih ada rahasia" lalu meneruskan ke
     * layar PIN, dan tiap permintaan mati di gerbang token — sehingga kasir melihat **"kode staf
     * atau PIN salah"** untuk masalah yang sama sekali bukan PIN, lalu menebak-nebak PIN-nya sampai
     * akunnya terkunci. Persis yang terjadi pada Imron, 2026-08-06.
     *
     * ⚠️ Yang TIDAK dibuang: kegagalan JARINGAN. `fetch` yang menolak tak pernah sampai ke sini,
     * dan status non-401 (500, 502) berarti servernya yang sedang sakit — bukan rahasianya yang
     * salah. Membuang kredensial karena server sedang restart berarti seluruh armada minta
     * dipasangkan ulang setiap kali kita deploy.
     */
    if (r.status === 401) { lupakanDevicePairing(); return undefined; }
    if (!r.ok) return undefined;
    const d = (await r.json()) as { access_token?: string };
    if (typeof d.access_token !== 'string' || d.access_token === '') return undefined;
    saveToken(d.access_token);
    return d.access_token;
  } catch {
    return undefined;
  }
}

/**
 * Meminta token baru ke `device-agent`. Mengembalikan `undefined` bila agen tak ada — itu keadaan
 * normal di pengembangan, dan **tidak normal** di terminal kasir.
 */
export async function tokenDariAgen(): Promise<string | undefined> {
  try {
    const r = await fetch(AGEN, { method: 'POST' });
    if (!r.ok) return undefined;
    const d = (await r.json()) as { access_token?: string };
    if (typeof d.access_token !== 'string' || d.access_token === '') return undefined;
    saveToken(d.access_token);
    return d.access_token;
  } catch {
    return undefined;
  }
}

/* ═══════════════ sesi STAF (manusia di depan mesin) ═══════════════════════ */

/**
 * ⚠️ **Dua token, dua umur, dua makna.** `osbpos.token` = identitas PERANGKAT (dari device-agent).
 * `osbpos.staf` = identitas MANUSIA (dari PIN). Menyimpannya di kunci yang sama akan membuat
 * logout kasir ikut mematikan perangkat — dan terminal yang mati tiap ganti shift tak akan dipakai
 * siapa pun.
 */
export interface StaffSession {
  readonly staffCode: string;
  readonly displayName: string;
  readonly permissions: readonly string[];
  /**
   * Token sesi staf dari server. ⛔ **Kosong pada sesi LURING** — tak ada yang bisa
   * menandatanganinya selain server, dan token karangan bukan pilihan.
   */
  readonly token: string;
  /**
   * ⭐ Sesi ini lahir dari salinan lokal (PIN diperiksa di tablet), bukan dari server.
   *
   * ⚠️ Wajib TERLIHAT di layar: izin di sesi ini berumur sampai 7 hari dan tak tahu apa-apa soal
   * pencabutan yang terjadi selama tablet luring. Sesi salinan yang menyamar jadi sesi biasa
   * membuat orang percaya pada jaminan yang sedang tidak berlaku.
   */
  readonly offline?: boolean;
}

let staffMemory: StaffSession | undefined;

export function staffSession(): StaffSession | undefined {
  if (staffMemory !== undefined) return staffMemory;
  const raw = globalThis.localStorage?.getItem(STAFF_KEY);
  if (raw === null || raw === undefined || raw === '') return undefined;
  try {
    staffMemory = JSON.parse(raw) as StaffSession;
  } catch {
    // ⛔ Isi rusak dibuang, bukan dipakai sebagian. Sesi setengah terbaca adalah identitas yang
    // tak bisa dipertanggungjawabkan.
    globalThis.localStorage?.removeItem(STAFF_KEY);
    return undefined;
  }
  return staffMemory;
}

export function saveStaffSession(s: StaffSession): void {
  staffMemory = s;
  globalThis.localStorage?.setItem(STAFF_KEY, JSON.stringify(s));
}

/** Keluar. ⭐ Token PERANGKAT sengaja tak ikut dibuang — terminalnya tetap terdaftar. */
export function logoutStaff(): void {
  staffMemory = undefined;
  globalThis.localStorage?.removeItem(STAFF_KEY);
}

export function hasPermission(perm: string): boolean {
  return staffSession()?.permissions.includes(perm) ?? false;
}
